import { ProductCreation, ProductRaw } from '@Arti-zen/package-backoffice'
import { Button, CircularProgress, Dialog } from '@mui/material'
import { useState } from 'react'
import styled from 'styled-components'
import { Theme } from 'theme/theme'
import ExempleProduct from './ExempleProduct'
import { useAppSelector } from 'hooks/useStore'
import { useNavigate } from 'react-router-dom'
import { pages } from 'navigation/pages'
import { sendProductsRawByChunk } from '../product.helpers'

type TPprops = {
  missingColumns: string[]
  extraColumns: string[]
  productsRaw: ProductRaw[]
  onModify: boolean
  onClose: () => void
}

enum ProductColumns {
  'description' = 'Désignation',
  'allDescription' = 'Description',
  'price' = 'Prix facturé HT',
  'coef' = 'Marge',
  'netPrice' = 'Coût HT',
  'vat' = 'TVA',
  'unit' = 'Unité',
  'id' = 'ID Produit',
}

export default function ErrorColumns({ onModify, missingColumns, extraColumns, productsRaw, onClose }: TPprops) {
  const { artisan } = useAppSelector((state) => state.artisan)

  const expectedColumns = [
    onModify ? 'id' : null,
    'description',
    'allDescription',
    'price',
    'coef',
    'netPrice',
    'vat',
    'unit',
  ].filter(Boolean)

  const currentColumns = Object.keys(productsRaw[0])

  const [extra, setExtra] = useState<Set<string>>(new Set(extraColumns))
  const [zones, setZones] = useState<Record<string, Set<string>>>(
    Object.fromEntries(
      expectedColumns.map((column) => [
        column,
        currentColumns.includes(column) || currentColumns.includes(ProductColumns[column] ?? '')
          ? new Set([ProductColumns[column] ?? column])
          : new Set(),
      ])
    )
  )
  const [availableOnSubmit, setAvailableOnSubmit] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<null | string>(null)
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const navigate = useNavigate()

  const getName = (zoneId: string): string => {
    switch (zoneId) {
      case 'description':
        return 'Désignation*'
      case 'allDescription':
        return 'Description'
      case 'price':
        return 'Prix facturé HT*'
      case 'coef':
        return 'Marge'
      case 'netPrice':
        return 'Coût HT'
      case 'vat':
        return 'TVA'
      case 'unit':
        return 'Unité'
      case 'id':
        return 'ID*'
    }
  }

  const handleDragStart = (event: React.DragEvent<HTMLDivElement>, item: string) => {
    event.dataTransfer.setData('text/plain', item)
  }

  const handleDrop = (event: React.DragEvent<HTMLDivElement>, zoneId: string) => {
    event.preventDefault()
    setAvailableOnSubmit(false)

    const item = event.dataTransfer.getData('text/plain')

    if (!item) return

    if (extra.has(item) && zoneId === 'extra') {
      return
    }

    if (zoneId === 'extra') {
      const newExtra = new Set([...extra])
      newExtra.add(item)
      setExtra(newExtra)
    }
    setZones((previous) => {
      const newZones = { ...previous }

      Object.keys(newZones).forEach((zone) => {
        if (newZones[zone].has(item)) newZones[zone].delete(item)
      })

      if (zoneId !== 'extra') newZones[zoneId].add(item)
      return newZones
    })

    if (extra.has(item)) {
      const newExtra = new Set([...extra])
      newExtra.delete(item)
      setExtra(newExtra)
    }
  }

  const onValid = () => {
    if (zones.description.size === 0 || zones.price.size === 0 || (onModify && zones.id.size === 0)) {
      setErrorMessage(
        `Les propriétés "description"${onModify ? ' , "id"' : ''} et "prix facturé HT" doivent avoir au moins une propriété.`
      )

      return
    }
    setErrorMessage(null)
    setAvailableOnSubmit(true)
  }

  const onSubmit = async () => {
    setIsLoading(true)
    const newProducts: ProductRaw[] = productsRaw.map((productRaw) => {
      const product = {}

      expectedColumns.forEach((prop) => {
        product[prop] = [...zones[prop]]
          .map((column) => productRaw?.[ProductColumns[column] ?? column])
          .filter(Boolean)
          .join(' - ')
          .trim()
      })

      return product as ProductRaw
    })

    const result = await sendProductsRawByChunk({
      productsToSend: newProducts,
      companyId: String(artisan.id),
      onModify,
    })
    setIsLoading(false)

    if (result.success === false) {
      setErrorMessage(result.error)
    } else {
      navigate(pages.connected.artisan.uploadProducts.navigate(+artisan.id), {
        state: {
          result,
        },
      })
    }
  }

  return (
    <Dialog open fullWidth={true} maxWidth="lg" onClose={onClose}>
      <Container style={isLoading ? { color: Theme.colors.blueArtizen } : {}}>
        {isLoading ? (
          <CircularProgress color="inherit" />
        ) : (
          <>
            <div>
              <p>Colonnes reçues</p>
              <p style={{ color: 'darkgray', fontSize: 13 }}>
                Toutes les colonnes laissées dans cet espace seront ignorées.
              </p>
              <Zone
                key={`zone-extra}`}
                onDragOver={(event) => event.preventDefault()}
                onDrop={(event) => handleDrop(event, 'extra')}
              >
                <Row style={{ flexWrap: 'wrap', minHeight: 33 }}>
                  {[...extra].map((column) => (
                    <Draggable
                      key={`draggable-${column}`}
                      draggable
                      onDragStart={(event) => handleDragStart(event, column)}
                    >
                      {column}
                    </Draggable>
                  ))}
                </Row>
              </Zone>
            </div>
            <div>
              <p>Colonnes attendues</p>
              <Row>
                {Object.entries(zones).map(([zoneId, zoneItems]) => (
                  <Zone
                    key={`zone-${zoneId}`}
                    onDragOver={(event) => event.preventDefault()}
                    onDrop={(event) => handleDrop(event, zoneId)}
                  >
                    <h3>{getName(zoneId)}</h3>
                    {[...zoneItems].map((item) => (
                      <Draggable
                        key={`dragged-${item}`}
                        draggable
                        onDragStart={(event) => handleDragStart(event, item)}
                      >
                        {item}
                      </Draggable>
                    ))}
                  </Zone>
                ))}
              </Row>
            </div>

            {errorMessage && <p style={{ color: 'red' }}>{errorMessage}</p>}

            {availableOnSubmit && (
              <ExempleProduct
                products={productsRaw.slice(0, 10)}
                newHeaders={zones}
                expectedColumns={expectedColumns as (keyof ProductCreation)[]}
              />
            )}

            <Button
              onClick={availableOnSubmit ? onSubmit : onValid}
              style={{
                display: 'flex',
                alignSelf: 'center',
                backgroundColor: Theme.colors.blueArtizen,
                color: 'white',
                width: '100%',
                marginTop: 20,
              }}
            >
              Valider
            </Button>
          </>
        )}
      </Container>
    </Dialog>
  )
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding: 40px;
  gap: 40px;
`

const Row = styled.div`
  display: flex;
  flex-direction: row;
  gap: 10px;
  flex: 1;
`

const Draggable = styled.div`
  text-align: center;
  color: ${Theme.colors.blueArtizen};
  border: 1px solid ${Theme.colors.blueArtizen};
  padding: 8px 16px;
  border-radius: 6px;
  background-color: #f6f6f6;
  font-size: 13px;
  font-weight: 500;
  cursor: grab;
`

const Zone = styled.div`
  background-color: #0a30af10;
  border-radius: 5px;
  border: 2px dashed #0a30af;
  padding: 15px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  text-align: center;
  h3 {
    margin: 5px 0 5px 0;
    font-size: 15px;
  }
`
